import CloseIcon from '@material-design-icons/svg/filled/close.svg?react';
import { useEffect, useState } from 'react';

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmationDialog({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
}: ConfirmationDialogProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Small delay to ensure the DOM is ready for animation
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before hiding
      const timer = setTimeout(() => setIsVisible(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isVisible) return null;

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity duration-200 ease-out ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onCancel}
      />

      <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
        <div
          className={`bg-neutral-800 rounded-lg w-full max-w-md relative pointer-events-auto shadow-2xl ${
            isAnimating ? 'animate-dialog-enter' : 'animate-dialog-exit'
          }`}
          style={{
            animation: isAnimating
              ? 'dialog-enter 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards'
              : 'dialog-exit 0.2s cubic-bezier(0.4, 0, 1, 1) forwards',
          }}
        >
          <div className="flex items-center justify-between p-6 pb-4">
            <h2 className="text-white text-xl font-medium">{title}</h2>
            <button
              type="button"
              onClick={onCancel}
              className="text-white/60 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-all duration-150 hover:scale-110"
            >
              <CloseIcon className="size-5" />
            </button>
          </div>

          <div className="px-6 pb-6">
            <p className="text-neutral-400 text-sm leading-relaxed">
              {message}
            </p>
          </div>

          <div className="flex gap-3 p-6 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 py-3 px-4 rounded-lg border border-white/20 text-white font-medium hover:bg-white/5 transition-all duration-150 hover:scale-[1.02] active:scale-[0.98]"
            >
              {cancelText}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="flex-1 py-3 px-4 rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700 transition-all duration-150 hover:scale-[1.02] active:scale-[0.98] hover:shadow-lg hover:shadow-teal-600/25"
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
